interface PortfolioData {
  intro: string;
  projects: Array<{ name: string; desc: string; tech: string[] }>;
  experience: Array<{ role: string; date: string; company: string; description: string }>;
  skills: Array<{ skillName: string }>;
  education: {
    School: string;
    Degree: string;
    Honors: string;
    Coursework: string;
    Certifications: string;
  };
}

interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

class ChatBotAPIHelper {
  private context: string = "";
  private history: ChatMessage[] = [];
  private isAvailable: boolean = true;
  private errorMessage: string = "";
  private isInitialized: boolean = false;
  private portfolioData: PortfolioData;
  private readonly GENERIC_ERROR = "I'm unable to process that request. Please try asking something else.";

  constructor(portfolioData: PortfolioData) {
    this.portfolioData = portfolioData;
  }

  private initialize(): void {
    if (this.isInitialized) return;
    
    this.context = this.buildContext(this.portfolioData);
    this.initSystem();
    this.isInitialized = true;
  }

  private buildContext(data: PortfolioData): string {
    const projectsStr = data.projects
      .map(p => `${p.name} (${p.tech.join(", ")}): ${p.desc}`)
      .join("; ");
    
    const experienceStr = data.experience
      .map(e => `${e.role} at ${e.company} (${e.date}): ${e.description}`)
      .join("; ");
    
    const skillsStr = data.skills
      .map(s => s.skillName)
      .join(", ");
    
    const educationStr = `${data.education.Degree} from ${data.education.School}. Honors: ${data.education.Honors}. Coursework: ${data.education.Coursework}. Certifications: ${data.education.Certifications}`;

    return `Intro: ${data.intro}. Education: ${educationStr}. Projects: ${projectsStr}. Experience: ${experienceStr}. Skills: ${skillsStr}.`;
  }

  private initSystem(): void {
    this.history.push({
      role: "system",
      content: `You are a helpful assistant for Eduardo's portfolio website. Answer questions about Eduardo's projects, experience, and skills using this data: ${this.context}. Keep responses concise and professional. The portfolio owner can be referred to as Eduardo, Eddie, or owner. Only discuss topics directly related to the portfolio content. Do not use markdown formatting. Politely decline requests outside the scope of portfolio discussion.`
    });
  }

  private sanitizeInput(input: string): string {
    let sanitized = input.trim().replace(/\s+/g, ' ');
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
    
    const MAX_LENGTH = 500;
    if (sanitized.length > MAX_LENGTH) {
      sanitized = sanitized.substring(0, MAX_LENGTH);
    }
    
    return sanitized;
  }

  private isValidInput(input: string): boolean {
    if (!input || input.trim().length < 2 || input.trim().length > 500) {
      return false;
    }

    const patterns = [
      new RegExp('(ignore|forget|disregard).{0,20}(previous|all|above|prior|instructions|prompts)', 'i'),
      new RegExp('you\\s+(are|must|should|will)\\s+(now|a|an)', 'i'),
      new RegExp('(new|override|change).{0,15}(instructions|role|system|prompt|context)', 'i'),
      new RegExp('\\[(SYSTEM|INST|USER|ASSISTANT)\\]', 'i'),
      new RegExp('<\\|[a-z]+\\|>', 'i'),
      new RegExp('(\\{\\{|\\}\\}|<%|%>|<\\?|\\?>)', ''),
      new RegExp('(eval|exec|function|import|require)\\s*\\(', 'i'),
    ];

    for (const pattern of patterns) {
      if (pattern.test(input)) {
        return false;
      }
    }

    const specialChars = (input.match(/[{}[\]<>$`\\]/g) || []).length;
    if (specialChars > 8) {
      return false;
    }

    if (/(.)\1{15,}/.test(input)) {
      return false;
    }

    const words = input.split(/\s+/);
    if (words.length > 100) {
      return false;
    }

    return true;
  }

  async chat(userInput: string): Promise<string> {
    if (!this.isAvailable) {
      return this.errorMessage;
    }

    if (!this.isValidInput(userInput)) {
      return this.GENERIC_ERROR;
    }

    const sanitizedInput = this.sanitizeInput(userInput);

    if (!this.isInitialized) {
      this.initialize();
    }

    this.history.push({ role: "user", content: sanitizedInput });

    try {
      const response = await fetch('/.netlify/functions/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: sanitizedInput,
          history: this.history
        })
      });

      if (!response.ok) {
        if (response.status === 429 || response.status === 503) {
          return "The chat service is currently busy. Please try again shortly.";
        }
        this.isAvailable = false;
        return "Chat is temporarily unavailable.";
      }

      const data = await response.json();
      const reply = data.reply || this.GENERIC_ERROR;
      
      this.history.push({ role: "assistant", content: reply });

      if (this.history.length > 7) {
        this.history = [this.history[0], ...this.history.slice(-6)];
      }

      return reply;
    } catch (error) {
      return this.GENERIC_ERROR;
    }
  }

  getAvailability(): boolean {
    return this.isAvailable;
  }

  reset(): void {
    this.history = this.history.slice(0, 1);
  }
}

export default ChatBotAPIHelper;